import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PullToRefresh,
  InfiniteScroll,
  List,
  SwipeAction,
  Button,
  Badge,
  Toast,
  Empty,
  Popup,
  Form,
  Selector,
  DatePicker,
  SearchBar,
  Skeleton,
  NavBar,
  Tabs,
  Tag,
} from 'antd-mobile';
import {
  FilterOutline,
  RightOutline,
  FireFill,
  ExclamationCircleFill,
  CheckCircleFill,
  ClockCircleFill,
  CloseCircleFill,
  SearchOutline,
} from 'antd-mobile-icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ticketApi } from '@/api/tickets';
import { useAuthStore } from '@/stores/authStore';
import { formatDate, getStatusText, getLevelText } from '@/utils/formatters';
import type { Ticket, TicketLevel, TicketStatus } from '@shared/types';

const LEVEL_OPTIONS = [
  { label: '全部等级', value: '' },
  { label: '🔴 红警', value: 'red' },
  { label: '🟡 黄警', value: 'yellow' },
];

const TAB_ITEMS = [
  { key: '', title: '全部' },
  { key: 'pending', title: '待整改' },
  { key: 'rectified', title: '已整改' },
  { key: 'verified', title: '已验收' },
];

export default function TicketListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();

  // 筛选状态
  const [filterVisible, setFilterVisible] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('status') || '');

  // 筛选表单值
  const [filters, setFilters] = useState({
    level: (searchParams.get('level') as TicketLevel) || '',
    status: (searchParams.get('status') as TicketStatus) || '',
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || '',
  });

  // 同步 URL 参数到状态
  useEffect(() => {
    const level = searchParams.get('level') as TicketLevel;
    const status = searchParams.get('status') as TicketStatus;
    if (level || status) {
      setFilters((prev) => ({
        ...prev,
        level: level || '',
        status: status || '',
      }));
      if (status) setActiveTab(status);
    }
  }, [searchParams]);

  // 无限滚动查询
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['tickets', 'list', filters, searchKeyword],
    queryFn: async ({ pageParam = 1 }) => {
      const params: any = {
        page: pageParam,
        page_size: 15,
        ...filters,
      };
      if (searchKeyword.trim()) {
        // 后端支持按工单号或描述搜索（需在 API 中实现）
        params.search = searchKeyword.trim();
      }
      const res = await ticketApi.list(params);
      return res.data.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.has_more) return undefined;
      return lastPage.page! + 1;
    },
    initialPageParam: 1,
    staleTime: 1000 * 30,
  });

  const tickets = data?.pages.flatMap((page) => page!.data) || [];
  const total = data?.pages[0]?.total || 0;

  // 下拉刷新
  const handleRefresh = async () => {
    await refetch();
    Toast.show({ icon: 'success', content: '刷新成功' });
  };

  // 应用筛选
  const applyFilters = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setActiveTab(newFilters.status);
    // 同步到 URL
    const params: Record<string, string> = {};
    if (newFilters.level) params.level = newFilters.level;
    if (newFilters.status) params.status = newFilters.status;
    if (newFilters.start_date) params.start_date = newFilters.start_date;
    if (newFilters.end_date) params.end_date = newFilters.end_date;
    setSearchParams(params);
    setFilterVisible(false);
  };

  // Tab 切换
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const newFilters = { ...filters, status: key as TicketStatus };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  // 重置筛选
  const resetFilters = () => {
    const empty = { level: '' as TicketLevel, status: '' as TicketStatus, start_date: '', end_date: '' };
    setFilters(empty);
    setActiveTab('');
    setSearchParams({});
    setFilterVisible(false);
  };

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      pending: '#ff4d4f',
      rectified: '#2E5BFF',
      verified: '#52c41a',
      closed: '#9CA3AF',
    };
    return map[status] || '#9CA3AF';
  };

  // 获取等级图标
  const getLevelIcon = (level: string) => {
    if (level === 'red') return <FireFill style={{ color: '#ff4d4f' }} />;
    return <ExclamationCircleFill style={{ color: '#faad14' }} />;
  };

  // 是否可以整改
  const canRectify = (ticket: Ticket) => {
    return (
      ticket.status === 'pending' &&
      (user?.id === ticket.responder_id || user?.role === 'admin')
    );
  };

  return (
    <div className="ticket-list-page">
      {/* 顶部导航 */}
      <div className="list-header">
        <div className="list-header-content">
          <h1 className="list-title">工单列表</h1>
          <span className="list-count">共 {total} 条</span>
        </div>
      </div>

      {/* 搜索栏 */}
      <div className="search-section">
        <SearchBar
          placeholder="搜索工单号或描述"
          value={searchKeyword}
          onChange={setSearchKeyword}
          onSearch={() => refetch()}
          className="list-search"
        />
        <div
          className="filter-btn"
          onClick={() => setFilterVisible(true)}
        >
          <FilterOutline />
          {(filters.level || filters.start_date) && (
            <div className="filter-dot" />
          )}
        </div>
      </div>

      {/* 状态 Tab */}
      <div className="tab-section">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="status-tabs"
        >
          {TAB_ITEMS.map((item) => (
            <Tabs.Tab title={item.title} key={item.key} />
          ))}
        </Tabs>
      </div>

      {/* 工单列表 */}
      <div className="list-content">
        <PullToRefresh onRefresh={handleRefresh}>
          {isLoading ? (
            <div className="skeleton-list">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton-card">
                  <Skeleton.Title animated />
                  <Skeleton.Paragraph lineCount={2} animated />
                </div>
              ))}
            </div>
          ) : isError ? (
            <Empty
              image={<SearchOutline style={{ fontSize: 64, color: '#ccc' }} />}
              description="加载失败，请下拉刷新重试"
            />
          ) : tickets.length === 0 ? (
            <Empty
              image={<SearchOutline style={{ fontSize: 64, color: '#ccc' }} />}
              description="暂无工单"
            />
          ) : (
            <>
              <List className="ticket-list">
                {tickets.map((ticket) => (
                  <SwipeAction
                    key={ticket.id}
                    rightActions={[
                      ...(canRectify(ticket)
                        ? [
                            {
                              key: 'rectify',
                              text: '整改',
                              color: '#2E5BFF',
                              onClick: () =>
                                navigate(`/tickets/${ticket.id}`, {
                                  state: { openRectify: true },
                                }),
                            },
                          ]
                        : []),
                      {
                        key: 'detail',
                        text: '详情',
                        color: '#52c41a',
                        onClick: () => navigate(`/tickets/${ticket.id}`),
                      },
                    ]}
                    className="ticket-swipe"
                  >
                    <List.Item
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                      className="ticket-list-item"
                    >
                      <div className="ticket-card">
                        <div className="ticket-card-header">
                          <div className="ticket-no">
                            {getLevelIcon(ticket.level)}
                            <span className="no-text">{ticket.ticket_no}</span>
                          </div>
                          <Tag
                            color={getStatusColor(ticket.status)}
                            fill="outline"
                            className="status-tag"
                          >
                            {getStatusText(ticket.status)}
                          </Tag>
                        </div>
                        <div className="ticket-desc">
                          {ticket.description.length > 60
                            ? ticket.description.slice(0, 60) + '…'
                            : ticket.description}
                        </div>
                        <div className="ticket-card-footer">
                          <div className="ticket-meta">
                            <span className="meta-item">
                              <span className="meta-label">提交人：</span>
                              {ticket.submitter_name}
                            </span>
                            <span className="meta-item">
                              <span className="meta-label">责任人：</span>
                              {ticket.responder_name}
                            </span>
                          </div>
                          <div className="ticket-time">
                            {formatDate(ticket.submit_time, 'MM-DD HH:mm')}
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  </SwipeAction>
                ))}
              </List>
              <InfiniteScroll
                loadMore={async () => { await fetchNextPage(); }}
                hasMore={!!hasNextPage}
              >
                {isFetchingNextPage ? (
                  <span className="loading-more">加载中…</span>
                ) : (
                  <span className="loading-more">没有更多了</span>
                )}
              </InfiniteScroll>
            </>
          )}
        </PullToRefresh>
      </div>

      {/* 筛选抽屉 */}
      <Popup
        visible={filterVisible}
        onMaskClick={() => setFilterVisible(false)}
        position="right"
        bodyStyle={{ width: '80vw', height: '100vh' }}
        className="filter-popup"
      >
        <div className="filter-drawer">
          <div className="filter-drawer-header">
            <h3>筛选条件</h3>
            <span className="filter-reset" onClick={resetFilters}>
              重置
            </span>
          </div>

          <div className="filter-body">
            {/* 严重等级 */}
            <div className="filter-group">
              <label className="filter-label">严重等级</label>
              <Selector
                options={LEVEL_OPTIONS}
                value={[filters.level]}
                onChange={(val) =>
                  setFilters((prev) => ({ ...prev, level: val[0] as TicketLevel }))
                }
                className="filter-selector"
              />
            </div>

            {/* 日期范围 */}
            <div className="filter-group">
              <label className="filter-label">提交日期</label>
              <div className="date-range">
                <div
                  className="date-input"
                  onClick={async () => {
                    const res = await DatePicker.prompt({
                      title: '开始日期',
                      precision: 'day',
                    });
                    if (res) {
                      const date = res.toISOString().slice(0, 10);
                      setFilters((prev) => ({ ...prev, start_date: date }));
                    }
                  }}
                >
                  {filters.start_date || '选择开始日期'}
                </div>
                <span className="date-sep">至</span>
                <div
                  className="date-input"
                  onClick={async () => {
                    const res = await DatePicker.prompt({
                      title: '结束日期',
                      precision: 'day',
                    });
                    if (res) {
                      const date = res.toISOString().slice(0, 10);
                      setFilters((prev) => ({ ...prev, end_date: date }));
                    }
                  }}
                >
                  {filters.end_date || '选择结束日期'}
                </div>
              </div>
            </div>
          </div>

          <div className="filter-footer">
            <Button
              block
              type="submit"
              size="large"
              onClick={() => applyFilters(filters)}
            >
              应用筛选
            </Button>
          </div>
        </div>
      </Popup>

      {/* 新建工单悬浮按钮 */}
      {user?.role !== 'responder' && (
        <div
          className="fab-create"
          onClick={() => navigate('/tickets/new')}
        >
          <span>+</span>
        </div>
      )}

      <style>{`
        .ticket-list-page {
          min-height: 100vh;
          background: #F5F7FA;
          padding-bottom: calc(16px + env(safe-area-inset-bottom));
        }

        .list-header {
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          padding: 16px 16px 24px;
        }

        .list-header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .list-title {
          color: white;
          font-size: 20px;
          font-weight: 700;
          margin: 0;
        }

        .list-count {
          color: rgba(255, 255, 255, 0.8);
          font-size: 13px;
        }

        .search-section {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: white;
          border-bottom: 1px solid #F0F2F5;
        }

        .list-search {
          flex: 1;
        }

        .list-search .adm-search-bar-input-box {
          background: #F5F7FA;
        }

        .filter-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F5F7FA;
          border-radius: 10px;
          color: #6B7280;
          font-size: 18px;
          position: relative;
          cursor: pointer;
        }

        .filter-btn:active {
          background: #E8ECF3;
        }

        .filter-dot {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 8px;
          height: 8px;
          background: #ff4d4f;
          border-radius: 50%;
        }

        .tab-section {
          background: white;
          border-bottom: 1px solid #F0F2F5;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .status-tabs .adm-tabs-header {
          border-bottom: none;
        }

        .status-tabs .adm-tabs-tab {
          font-size: 14px;
          padding: 12px 16px;
        }

        .status-tabs .adm-tabs-tab-active {
          font-weight: 600;
        }

        .list-content {
          padding: 8px 12px;
        }

        .skeleton-list {
          padding: 8px;
        }

        .skeleton-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
        }

        .ticket-list {
          --padding-left: 0;
          --padding-right: 0;
          --border-top: none;
          --border-bottom: none;
        }

        .ticket-list .adm-list-body {
          background: transparent;
        }

        .ticket-list-item {
          padding: 0 !important;
        }

        .ticket-list-item .adm-list-item-content {
          padding: 0 !important;
          border-bottom: none !important;
        }

        .ticket-swipe {
          margin-bottom: 12px;
          border-radius: 12px;
          overflow: hidden;
        }

        .ticket-swipe .adm-list-item {
          background: white;
        }

        .ticket-card {
          padding: 16px;
          background: white;
        }

        .ticket-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .ticket-no {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 15px;
          font-weight: 600;
          color: #1F2433;
        }

        .no-text {
          font-family: var(--adm-font-family);
          font-variant-numeric: tabular-nums;
        }

        .status-tag {
          font-size: 12px !important;
          padding: 2px 8px !important;
          border-radius: 4px !important;
        }

        .ticket-desc {
          font-size: 14px;
          color: #6B7280;
          line-height: 1.6;
          margin-bottom: 12px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .ticket-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }

        .ticket-meta {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .meta-item {
          font-size: 12px;
          color: #9CA3AF;
        }

        .meta-label {
          color: #bbb;
        }

        .ticket-time {
          font-size: 12px;
          color: #bbb;
          flex-shrink: 0;
        }

        .loading-more {
          display: block;
          text-align: center;
          padding: 16px;
          font-size: 13px;
          color: #9CA3AF;
        }

        .filter-drawer {
          height: 100%;
          display: flex;
          flex-direction: column;
          background: #F5F7FA;
        }

        .filter-drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: white;
          border-bottom: 1px solid #F0F2F5;
        }

        .filter-drawer-header h3 {
          margin: 0;
          font-size: 17px;
          font-weight: 600;
        }

        .filter-reset {
          font-size: 14px;
          color: #2E5BFF;
          cursor: pointer;
        }

        .filter-body {
          flex: 1;
          padding: 16px;
          overflow-y: auto;
        }

        .filter-group {
          margin-bottom: 24px;
        }

        .filter-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #1F2433;
          margin-bottom: 12px;
        }

        .filter-selector .adm-selector-item {
          border-radius: 8px;
        }

        .date-range {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .date-input {
          flex: 1;
          padding: 12px;
          background: white;
          border-radius: 8px;
          font-size: 14px;
          color: #6B7280;
          text-align: center;
          border: 1px solid #E8ECF3;
          cursor: pointer;
        }

        .date-input:active {
          background: #F0F2F5;
        }

        .date-sep {
          font-size: 13px;
          color: #9CA3AF;
        }

        .filter-footer {
          padding: 16px;
          background: white;
          border-top: 1px solid #F0F2F5;
        }

        .fab-create {
          position: fixed;
          right: 20px;
          bottom: calc(80px + env(safe-area-inset-bottom));
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
          font-weight: 300;
          box-shadow: 0 4px 16px rgba(22, 119, 255, 0.35);
          cursor: pointer;
          z-index: 100;
          transition: transform 0.2s ease;
        }

        .fab-create:active {
          transform: scale(0.92);
        }

        .adm-empty {
          padding: 48px 16px;
        }
      `}</style>
    </div>
  );
}